import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import DesktopLayout from '../../../components/layout/DesktopLayout';
import AdDetailModal from '../../../components/advertising/AdDetailModal/AdDetailModal';
import api from '../../../services/api';
import './index.css';

interface QueueItem {
  id: string;
  type: 'ad';
  title: string;
  description?: string;
  category?: string;
  region?: string;
  userName?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

const AdminQueuePage = () => {
  const { hasRole } = useAuth();
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Ad detail modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAdId, setSelectedAdId] = useState<string | null>(null);

  useEffect(() => {
    if (!hasRole(['moderator', 'admin', 'super_admin'])) {
      setMessage({ type: 'error', text: '您沒有權限訪問此頁面' });
      setIsLoading(false);
      return;
    }
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<QueueItem[]>('/api/admin/queue');

      if (response.success && response.data) {
        setQueue(response.data || []);
      } else {
        setMessage({ type: 'error', text: response.error?.message || '載入失敗' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '網路錯誤' });
      console.error('Queue fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      const response = await api.put(`/api/admin/queue/${id}/approve`, {});

      if (response.success) {
        setMessage({ type: 'success', text: '已通過審核' });
        fetchQueue();
      } else {
        setMessage({ type: 'error', text: response.error?.message || '操作失敗' });
      }
    } catch {
      setMessage({ type: 'error', text: '網路錯誤' });
    }
  };

  const handleReject = async () => {
    if (!selectedItem) return;

    try {
      const response = await api.put(`/api/admin/queue/${selectedItem.id}/reject`, {
        reason: rejectReason,
      });

      if (response.success) {
        setMessage({ type: 'success', text: '已拒絕審核' });
        setShowRejectDialog(false);
        setRejectReason('');
        setSelectedItem(null);
        fetchQueue();
      } else {
        setMessage({ type: 'error', text: response.error?.message || '操作失敗' });
      }
    } catch {
      setMessage({ type: 'error', text: '網路錯誤' });
    }
  };

  const openRejectDialog = (item: QueueItem) => {
    setSelectedItem(item);
    setShowRejectDialog(true);
  };

  const closeRejectDialog = () => {
    setShowRejectDialog(false);
    setRejectReason('');
    setSelectedItem(null);
  };

  // Get all ad IDs for navigation
  const allAdIds = queue.map(item => item.id);

  // Open ad detail modal
  const openDetailModal = (adId: string) => {
    setSelectedAdId(adId);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedAdId(null);
  };

  const handleAdChange = (adId: string) => {
    setSelectedAdId(adId);
  };

  // Handle approve from modal
  const handleModalApprove = async (adId: string) => {
    await handleApprove(adId);
    // Close modal after approval
    setShowDetailModal(false);
    setSelectedAdId(null);
  };

  // Handle reject from modal - open reject dialog
  const handleModalReject = (adId: string) => {
    // Find the item and open reject dialog
    const item = queue.find(i => i.id === adId);
    if (item) {
      openRejectDialog(item);
      setShowDetailModal(false);
    }
  };

  return (
    <div className="admin-queue-page">
      <DesktopLayout>
      <main className="admin-queue-page__main">
        <div className="admin-queue-page__container">
          <header className="admin-queue-page__header">
            <h1 className="admin-queue-page__title">待審廣告</h1>
          </header>

      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="alert-close">×</button>
        </div>
      )}

      {isLoading ? (
        <div className="loading-state">載入中...</div>
      ) : queue.length === 0 ? (
        <div className="empty-state">
          <p>目前沒有待審核的廣告</p>
        </div>
      ) : (
        <div className="queue-list">
          {queue.map((item) => (
            <div key={item.id} className="queue-item">
              <div className="queue-item__header">
                <span className="queue-item__date">
                  {new Date(item.createdAt).toLocaleString('zh-TW')}
                </span>
              </div>

              <div className="queue-item__content">
                <h3 className="queue-item__title">{item.title}</h3>

                {item.description && (
                  <p className="queue-item__description">{item.description}</p>
                )}

                <div className="queue-item__meta">
                  {item.category && <span>類別：{item.category}</span>}
                  {item.region && <span>地區：{item.region}</span>}
                  {item.userName && <span>申請人：{item.userName}</span>}
                </div>
              </div>

              <div className="queue-item__actions">
                <button
                  className="action-btn action-btn--reject"
                  onClick={() => openRejectDialog(item)}
                >
                  拒絕
                </button>
                <button
                  className="action-btn action-btn--approve"
                  onClick={() => handleApprove(item.id)}
                >
                  通過
                </button>
                <button
                  className="action-btn action-btn--view"
                  onClick={() => openDetailModal(item.id)}
                >
                  查看詳情
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject Dialog */}
      {showRejectDialog && selectedItem && (
        <div className="modal-overlay" onClick={closeRejectDialog}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2>拒絕審核</h2>
              <button onClick={closeRejectDialog} className="modal-close">×</button>
            </div>

            <div className="modal__body">
              <p>請輸入拒絕原因（將通知申請人）：</p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="請說明拒絕原因..."
                rows={4}
                maxLength={500}
              />
              <small>{rejectReason.length}/500</small>
            </div>

            <div className="modal__footer">
              <button onClick={closeRejectDialog} className="btn btn--secondary">
                取消
              </button>
              <button
                onClick={handleReject}
                className="btn btn--danger"
                disabled={!rejectReason.trim()}
              >
                確認拒絕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ad Detail Modal */}
      <AdDetailModal
        isOpen={showDetailModal}
        onClose={closeDetailModal}
        adId={selectedAdId}
        allAdIds={allAdIds}
        onAdChange={handleAdChange}
        useAdminApi={true}
        hideViewFullDetails={true}
        showApprovalActions={true}
        onApprove={handleModalApprove}
        onReject={handleModalReject}
      />
        </div>
      </main>
      </DesktopLayout>
    </div>
  );
};

export default AdminQueuePage;
